// "Road network sources": what the passing-zone rules took from where (Safety Overview Part B).
// Every shown road carries its road-network block (the segment payload's `network`): the
// features used, and per SOP condition how many came from each source, where each source
// had data and whether it was complete there. This panel sums them over the shown roads.
import React, { useEffect, useState } from 'react';
import { NET_CONDITIONS, NET_SOURCES } from './rules';
import { fetchRoadDeletions, restoreRoadElement } from './safetyOverviewApi';
import { segmentName } from '../Overview/overviewModel';

const STATUS_TEXT = {
  ok: 'data', none: 'no data on the road', unavailable: 'not available for this state', error: 'failed (retried within the hour)', off: 'off',
};

export function summarizeNetwork(segments) {
  const withNet = segments.filter((s) => s.network && !s.network.error);
  const cond = {};
  const status = {};
  const notes = [];
  withNet.forEach((s) => {
    Object.entries(s.network.sources || {}).forEach(([src, v]) => {
      status[src] = status[src] || {};
      status[src][v.status] = (status[src][v.status] || 0) + 1;
      if (v.note && v.status !== 'none') notes.push({ seg: segmentName(s), src, note: v.note, status: v.status });
      if (v.label && v.status === 'ok') status[src].label = v.label;
    });
    Object.entries(s.network.provenance || {}).forEach(([c, p]) => {
      const row = cond[c] || (cond[c] = { count: 0, uncoveredMi: 0, sources: {} });
      row.count += p.count;
      row.uncoveredMi += p.uncoveredMi || 0;
      Object.entries(p.sources || {}).forEach(([src, u]) => {
        const r = row.sources[src] || (row.sources[src] = { count: 0, coverageMi: 0, complete: false, supplies: false });
        r.count += u.count || 0;
        r.coverageMi += u.coverageMi || 0;
        r.complete = r.complete || Boolean(u.complete);
        r.supplies = r.supplies || Boolean(u.supplies);
      });
    });
  });
  const attribution = Array.from(new Set(withNet.flatMap((s) => s.network.attribution || [])));
  const rails = { count: 0, labels: new Set() };
  withNet.forEach((s) => {
    const g = (s.network.provenance || {}).guardrail;
    if (g) { rails.count += g.count || 0; if (g.label) rails.labels.add(g.label); }
  });
  return { roads: withNet.length, cond, status, notes, attribution, rails: { count: rails.count, from: Array.from(rails.labels) },
    failed: segments.filter((s) => s.network && s.network.error).length };
}

/** The road elements deleted by hand in the shown states, with Restore for those who may. */
function DeletedElements({ states, edits, canDelete, onRestored }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const key = states.join(',');
  useEffect(() => {
    let live = true;
    Promise.all(key.split(',').filter(Boolean).map((st) => fetchRoadDeletions(st).then((r) => r.deletions || []).catch(() => [])))
      .then((lists) => { if (live) setRows(lists.flat()); });
    return () => { live = false; };
  }, [key, edits]);
  if (!rows.length) return null;
  const typeLabel = Object.fromEntries(NET_CONDITIONS.map(([k, label]) => [k, label]));
  const srcName = Object.fromEntries(NET_SOURCES.map(([k, label]) => [k, label]));
  const restore = async (r) => {
    setBusy(r.id);
    setError('');
    try {
      await restoreRoadElement(r.id);
      await onRestored(r.state);
    } catch (e) { setError(e.message); }
    setBusy(null);
  };
  return (
    <details className="so-net-deleted" open>
      <summary><b>{rows.length} road element{rows.length === 1 ? '' : 's'} deleted by hand</b>
        <span className="safety-muted"> · left out of the map, the rules, the correlation and the PDF</span></summary>
      <ul className="so-net-status">
        {rows.map((r) => (
          <li key={r.id}>
            {typeLabel[r.type] || r.type} · {r.label || r.ref || ''} · {srcName[r.src] || r.src}
            {r.road ? ` · ${r.road}` : ''}{r.mp != null ? ` · MP ${Number(r.mp).toFixed(2)}` : ''}
            <span className="safety-muted"> · by {r.deleted_by} {r.deleted_at}{r.reason ? ` · “${r.reason}”` : ''}</span>
            {canDelete && canDelete(r.src) && (
              <> <button type="button" className="so-link" disabled={busy === r.id} onClick={() => restore(r)}>
                {busy === r.id ? 'restoring…' : 'restore'}</button></>
            )}
          </li>
        ))}
      </ul>
      {error && <div className="safety-error">{error}</div>}
    </details>
  );
}

export default function NetworkSources({ segments, rules, edits = 0, canDelete = null, onRestored = null }) {
  const sum = summarizeNetwork(segments);
  const states = Array.from(new Set(segments.filter((s) => s.network).map((s) => s.state).filter(Boolean))).sort();
  if (!sum.roads) return <p className="safety-muted">No road-network data for the shown roads.</p>;
  const order = rules.network.priority;
  const srcName = Object.fromEntries(NET_SOURCES.map(([k, label]) => [k, label]));
  return (
    <div className="so-net-sources">
      <p className="safety-muted">
        {sum.roads} road{sum.roads === 1 ? '' : 's'} · sources in the order {order.map((k) => srcName[k]).join(' → ')} ·{' '}
        {rules.network.mode === 'fill' ? 'lower sources fill the gaps only' : 'every source adds what the others miss'}.
        Change it in Settings → Filters.
      </p>
      <table className="ov-table">
        <thead>
          <tr>
            <th>Feature</th><th>Used</th>
            {order.map((k) => <th key={k}>{srcName[k]}{sum.status[k]?.label && sum.status[k].label !== srcName[k] ? ` (${sum.status[k].label})` : ''}</th>)}
            <th title="Miles of road where none of the chosen sources has data for this feature">No data</th>
          </tr>
        </thead>
        <tbody>
          {NET_CONDITIONS.map(([c, label]) => {
            const rc = rules.network.conditions[c];
            const row = sum.cond[c];
            if (!rc.on || !row) {
              return <tr key={c} className="safety-muted"><td>{label}</td><td colSpan={order.length + 2}>off</td></tr>;
            }
            return (
              <tr key={c}>
                <td>{label}{rc.source !== 'auto' ? <span className="safety-muted"> · {srcName[rc.source]} only</span> : null}</td>
                <td><b>{row.count}</b></td>
                {order.map((k) => {
                  const u = row.sources[k];
                  if (!u || (rc.source !== 'auto' && rc.source !== k)) return <td key={k} className="safety-muted">—</td>;
                  if (!u.supplies) return <td key={k} className="safety-muted" title="This source has no such data">n/a</td>;
                  return (
                    <td key={k} title={`${u.coverageMi.toFixed(1)} mi consulted`}>
                      {u.count}
                      {u.coverageMi > 0 && <span className={u.complete ? 'safety-muted' : 'so-net-part'}>{u.complete ? ' · complete' : ' · partial'}</span>}
                    </td>
                  );
                })}
                <td>{row.uncoveredMi > 0.05 ? `${row.uncoveredMi.toFixed(1)} mi` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <ul className="so-net-status">
        {order.map((k) => {
          const st = sum.status[k] || {};
          const parts = Object.entries(st).filter(([s]) => s !== 'label').map(([s, n]) => `${n} road${n === 1 ? '' : 's'}: ${STATUS_TEXT[s] || s}`);
          return <li key={k}><b>{srcName[k]}</b> — {parts.join(' · ') || 'no answer'}</li>;
        })}
        {sum.failed > 0 && <li>{sum.failed} road{sum.failed === 1 ? '' : 's'}: the road network could not be read</li>}
      </ul>
      {sum.notes.length > 0 && (
        <details>
          <summary className="safety-muted">{sum.notes.length} note{sum.notes.length === 1 ? '' : 's'}</summary>
          <ul className="so-net-status">
            {sum.notes.map((n, i) => <li key={i}>{n.seg} · {srcName[n.src]}: {n.note}</li>)}
          </ul>
        </details>
      )}
      <p className="so-net-status">
        <b>Existing guardrails:</b> {sum.rails.count}{sum.rails.from.length ? ` from ${sum.rails.from.join(', ')}` : ''}
        <span className="safety-muted"> · they shield the GRW warrants on their side of travel
          {rules.grwShield && rules.grwShield.ignoreInventory ? ' — switched off in Settings → Filters' : ''}</span>
      </p>
      <DeletedElements states={states} edits={edits} canDelete={canDelete} onRestored={onRestored || (() => {})} />
      {sum.attribution.map((a) => <p key={a} className="safety-muted">{a}</p>)}
    </div>
  );
}
